import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPencil, faLocationDot, faAddressBook, faWrench, faInbox,
  faFileInvoice, faShieldHalved, faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { customerService } from '@/services/customer.service';
import { equipmentService } from '@/services/equipment.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonPage } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes/routes';
import { CustomerFormModal } from './components/CustomerFormModal';
import { LocationContactsManager } from './components/LocationContactsManager';

type Tab = 'info' | 'locations' | 'equipment' | 'audit';

function StatCard({ icon, label, value, soon }: { icon: typeof faLocationDot; label: string; value?: number; soon?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1.5 text-2xl font-semibold text-foreground">
        {soon ? <span className="text-sm font-medium text-muted-foreground">Coming Soon</span> : (value ?? '—')}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();
  const [tab, setTab] = useState<Tab>('info');
  const [editOpen, setEditOpen] = useState(false);

  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerService.get(id!),
    enabled: !!id,
  });
  const { data: stats } = useQuery({
    queryKey: ['customer-stats', id],
    queryFn: () => customerService.stats(id!),
    enabled: !!id,
  });
  const { data: equipment } = useQuery({
    queryKey: ['customer-equipment', id],
    queryFn: () => equipmentService.list({ customerId: id!, pageSize: 100 }),
    enabled: !!id && tab === 'equipment',
  });

  const canEdit = can('customer:update');

  if (isLoading) return <SkeletonPage />;
  if (isError || !customer) return (
    <div className="space-y-4">
      <button onClick={() => navigate(ROUTES.CUSTOMERS)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" /> Back to Customers
      </button>
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-6 py-10 text-center text-sm text-destructive">
        Customer not found or could not be loaded.
      </div>
    </div>
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'info', label: 'Information' },
    { key: 'locations', label: 'Locations' },
    { key: 'equipment', label: 'Equipment' },
    { key: 'audit', label: 'Audit History' },
  ];

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(ROUTES.CUSTOMERS)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" /> Back to Customers
      </button>

      {/* Header */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">{customer.organizationName ?? customer.name}</h1>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${customer.isActive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
              {customer.status}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span><span className="font-mono">{customer.customerCode ?? '—'}</span></span>
            <span>GST: {customer.gstNumber ?? '—'}</span>
            <span>Username: <span className="font-mono">{customer.username ?? '—'}</span></span>
          </div>
        </div>
        {canEdit && (
          <Button variant="secondary" onClick={() => setEditOpen(true)}>
            <FontAwesomeIcon icon={faPencil} className="mr-2 h-3.5 w-3.5" /> Edit
          </Button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard icon={faLocationDot} label="Locations" value={stats?.locations} />
        <StatCard icon={faAddressBook} label="Contacts" value={stats?.contacts} />
        <StatCard icon={faWrench} label="Equipment" value={stats?.equipment} />
        <StatCard icon={faInbox} label="Open Inward Records" value={stats?.openIntakes} />
        <StatCard icon={faFileInvoice} label="Service Requests" soon />
        <StatCard icon={faShieldHalved} label="AMC Contracts" soon />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'info' && (
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Customer Name" value={customer.name} />
          <Field label="Organization Name" value={customer.organizationName} />
          <Field label="Customer Code" value={customer.customerCode} />
          <Field label="Email" value={customer.email} />
          <Field label="Phone" value={customer.phone} />
          <Field label="Alternate Phone" value={customer.alternatePhone} />
          <Field label="GST Number" value={customer.gstNumber} />
          <Field label="Website" value={customer.website} />
          <Field label="Industry" value={customer.industry} />
          <Field label="Address Line 1" value={customer.addressLine1} />
          <Field label="Address Line 2" value={customer.addressLine2} />
          <Field label="City" value={customer.city} />
          <Field label="State" value={customer.state} />
          <Field label="Country" value={customer.country} />
          <Field label="Postal Code" value={customer.postalCode} />
          <Field label="Notes" value={customer.notes} />
        </div>
      )}

      {tab === 'locations' && (
        <div className="rounded-lg border border-border bg-card p-5">
          <LocationContactsManager customerId={customer.id} canEdit={canEdit} />
        </div>
      )}

      {tab === 'equipment' && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[600px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                <th className="px-4 py-3 font-medium text-foreground">Serial</th>
                <th className="px-4 py-3 font-medium text-foreground">Asset</th>
                <th className="px-4 py-3 font-medium text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {equipment && equipment.items.length > 0 ? equipment.items.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-foreground">{e.equipmentName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.serialNumber}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.assetNumber}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.status}</td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No equipment for this customer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audit' && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Audit history view — Coming Soon.
        </div>
      )}

      <CustomerFormModal
        open={editOpen}
        editTarget={customer}
        onClose={() => setEditOpen(false)}
        onSaved={() => { void qc.invalidateQueries({ queryKey: ['customer', id] }); void qc.invalidateQueries({ queryKey: ['customer-stats', id] }); }}
      />
    </div>
  );
}
